import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { AuthContext } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { 
  UserRole, 
  Permission, 
  getRolePermissions, 
  hasPermission, 
  hasResourceAccess 
} from '@/types/permissions';
import { usePermissionOverrides } from '@/hooks/usePermissionOverrides';

interface UserProfile {
  id: string;
  nome: string;
  role: UserRole;
  ativo: boolean;
  aprovado_em: string | null;
  aprovado_por: string | null;
  telefone: string | null;
  vendedor_id?: string | null; // Para parceiros
  permission_overrides?: Permission[]; // Permissões personalizadas
  /** Se true, o parceiro pode cadastrar outros parceiros em sua rede */
  pode_criar_rede_parceiros?: boolean;
  /** Data em que assinou o contrato de plataforma (obrigatório para parceiro/vendedor) */
  contrato_plataforma_assinado_em?: string | null;
}

interface AuthorizationContextType {
  userProfile: UserProfile | null;
  userRole: UserRole | null;
  userPermissions: Permission[];
  isLoading: boolean;
  error: string | null;
  
  // Funções de verificação de permissões
  can: (permission: string) => boolean;
  canAccess: (resource: string, action: string) => boolean;
  hasRole: (role: UserRole) => boolean;
  
  // Funções de atualização
  refreshUserProfile: () => Promise<void>;
  updateUserRole: (newRole: UserRole) => Promise<void>;
  
  // Funções de gerenciamento de overrides (apenas para admin)
  canManageOverrides: boolean;
  refreshOverrides: () => Promise<void>;
}

const AuthorizationContext = createContext<AuthorizationContextType | undefined>(undefined);

export const AuthorizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Não usar useAuth diretamente para evitar dependência circular
  // Os dados serão fornecidos pelo AuthProvider via contexto
  const authContext = useContext(AuthContext);
  const { user, session } = authContext || { user: null, session: null };
  
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const userProfileRef = React.useRef<UserProfile | null>(null);
  
  // Atualizar ref quando o estado mudar
  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);

  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Se não há contexto de auth ainda, usar valores padrão mas ainda renderizar o Provider
  const isAuthContextReady = !!authContext;

  // Hook para gerenciar overrides (apenas se usuário estiver logado)
  const {
    overrides,
    isLoading: overridesLoading,
    error: overridesError,
    getOverride,
    validateOverride
  } = usePermissionOverrides(user?.id || '');

  // Função para buscar o perfil do usuário
  const fetchUserProfile = useCallback(async (userId: string) => {
    try {
      // Só mostrar loading se não tivermos um perfil ainda
      // Isso evita que a página inteira "recarregue" (desmonte) via ProtectedRoute
      // durante atualizações em segundo plano (ex: ao voltar para a aba)
      if (!userProfileRef.current) {
        setIsLoading(true);
      }
      setError(null);

      // if (import.meta.env.DEV) {
      //   console.log('🔍 Buscando perfil do usuário:', userId);
      // }

      // Primeiro, vamos verificar se a tabela profiles existe
      const { data: tableCheck, error: tableError } = await supabase
        .from('profiles')
        .select('count')
        .limit(1);

      if (tableError) {
        console.warn('⚠️ Tabela profiles não encontrada, criando perfil padrão');
        // Se a tabela não existe, criar um perfil padrão
        const defaultProfile: UserProfile = {
          id: userId,
          nome: user?.email || 'Usuário',
          role: 'user' as UserRole,
          ativo: true,
          aprovado_em: new Date().toISOString(),
          aprovado_por: 'system',
          telefone: null,
        };
        
        setUserProfile(defaultProfile);
        setUserRole(defaultProfile.role);
        setUserPermissions(getRolePermissions(defaultProfile.role));
        return;
      }

      const { data, error: profileError } = await supabase
        .from('profiles')
        .select(`
          id,
          nome,
          role,
          ativo,
          aprovado_em,
          aprovado_por,
          telefone,
          vendedor_id,
          permission_overrides,
          pode_criar_rede_parceiros,
          contrato_plataforma_assinado_em
        `)
        .eq('id', userId)
        .single();

      if (profileError) {
        console.warn('⚠️ Erro ao buscar perfil, criando perfil padrão:', profileError.message);
        // Se não conseguir buscar o perfil, criar um padrão
        const defaultProfile: UserProfile = {
          id: userId,
          nome: user?.email || 'Usuário',
          role: 'user' as UserRole,
          ativo: true,
          aprovado_em: new Date().toISOString(),
          aprovado_por: 'system',
          telefone: null,
        };
        
        setUserProfile(defaultProfile);
        setUserRole(defaultProfile.role);
        setUserPermissions(getRolePermissions(defaultProfile.role));
        return;
      }

      if (!data) {
        throw new Error('Perfil do usuário não encontrado');
      }

      // Para parceiros com senha provisória, permitir acesso mesmo com ativo: false
      const temSenhaProvisoria = user?.user_metadata?.senha_provisoria;
      if (!data.ativo && !temSenhaProvisoria) {
        throw new Error('Usuário inativo. Entre em contato com o administrador.');
      }

      const profile: UserProfile = {
        id: data.id,
        nome: data.nome,
        role: data.role,
        ativo: data.ativo,
        aprovado_em: data.aprovado_em,
        aprovado_por: data.aprovado_por,
        telefone: data.telefone,
        vendedor_id: data.vendedor_id,
        permission_overrides: data.permission_overrides || [],
        pode_criar_rede_parceiros: data.pode_criar_rede_parceiros ?? false,
        contrato_plataforma_assinado_em: data.contrato_plataforma_assinado_em ?? null,
      };

      // if (import.meta.env.DEV) {
      //   console.log('✅ Perfil carregado com sucesso:', profile);
      //   console.log('🔑 Role do usuário:', profile.role);
      // }

      setUserProfile(profile);
      setUserRole(profile.role);
      
      // As permissões serão combinadas nas funções can() e canAccess()
      // usando os overrides do hook
      const basePermissions = getRolePermissions(profile.role);
      setUserPermissions(basePermissions);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      console.error('❌ Erro ao buscar perfil do usuário:', err);
      
      // Em caso de erro, criar perfil padrão
      const defaultProfile: UserProfile = {
        id: userId,
        nome: user?.email || 'Usuário',
        role: 'user' as UserRole,
        ativo: true,
        aprovado_em: new Date().toISOString(),
        aprovado_por: 'system',
        telefone: null,
      };
      
      setUserProfile(defaultProfile);
      setUserRole(defaultProfile.role);
      setUserPermissions(getRolePermissions(defaultProfile.role));
    } finally {
      setIsLoading(false);
    }
  }, [user?.email]);

  // Função para atualizar o perfil do usuário
  const refreshUserProfile = useCallback(async () => {
    if (user?.id) {
      await fetchUserProfile(user.id);
    }
  }, [user?.id, fetchUserProfile]);

  // Função para atualizar o papel do usuário (apenas para admins)
  const updateUserRole = useCallback(async (newRole: UserRole) => {
    if (!userProfile || userProfile.role !== 'admin') {
      throw new Error('Apenas administradores podem alterar papéis de usuário');
    }

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userProfile.id);

      if (updateError) {
        throw new Error(`Erro ao atualizar papel: ${updateError.message}`);
      }

      // Atualizar o contexto local
      setUserRole(newRole);
      setUserPermissions(getRolePermissions(newRole));

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      throw err;
    }
  }, [userProfile]);

  // Funções de verificação de permissões
  const can = useCallback((permission: string): boolean => {
    if (!userRole || !permission) return false;
    try {
      // Verificar permissões base do role
      const hasBasePermission = hasPermission(userRole, permission);
      
      // Verificar overrides personalizados do hook
      const override = getOverride(permission);
      if (override) {
        return override.granted; // Override específico tem prioridade
      }
      
      return hasBasePermission;
    } catch (error) {
      console.error('Erro ao verificar permissão:', error);
      return false;
    }
  }, [userRole, getOverride]);

  const canAccess = useCallback((resource: string, action: string): boolean => {
    if (!userRole || !resource || !action) return false;
    try {
      // Verificar permissões base do role
      const hasBaseAccess = hasResourceAccess(userRole, resource, action);
      
      // Verificar overrides personalizados do hook
      const override = overrides.find(o => 
        o.resource === resource && 
        (o.action === action || o.action === 'manage')
      );
      
      if (override) {
        return override.granted; // Override específico tem prioridade
      }
      
      return hasBaseAccess;
    } catch (error) {
      console.error('Erro ao verificar acesso ao recurso:', error);
      return false;
    }
  }, [userRole, overrides]);

  const hasRole = useCallback((role: UserRole): boolean => {
    if (!userRole || !role) return false;
    return userRole === role;
  }, [userRole]);

  // Funções de gerenciamento de overrides (apenas para admin)
  const canManageOverrides = userRole === 'admin';
  
  const refreshOverrides = useCallback(async () => {
    // O hook já gerencia o refresh automaticamente
  }, []);

  // Efeito para buscar o perfil quando o usuário mudar
  useEffect(() => {
    // Só buscar perfil se o contexto de auth estiver pronto
    if (!isAuthContextReady) {
      setIsLoading(false);
      return;
    }
    
    if (user?.id && session) {
      fetchUserProfile(user.id);
    } else {
      setUserProfile(null);
      setUserRole(null);
      setUserPermissions([]);
      setIsLoading(false);
    }
  }, [user?.id, session, fetchUserProfile, isAuthContextReady]);

  const value: AuthorizationContextType = React.useMemo(() => ({
    userProfile,
    userRole,
    userPermissions,
    isLoading: isLoading || overridesLoading,
    error: error || overridesError,
    can,
    canAccess,
    hasRole,
    refreshUserProfile,
    updateUserRole,
    canManageOverrides,
    refreshOverrides,
  }), [
    userProfile,
    userRole,
    userPermissions,
    isLoading,
    overridesLoading,
    error,
    overridesError,
    can,
    canAccess,
    hasRole,
    refreshUserProfile,
    updateUserRole,
    canManageOverrides,
    refreshOverrides,
  ]);

  // Garantir que temos o contexto de auth antes de prosseguir com a renderização dos filhos
  if (!authContext) {
    if (import.meta.env.DEV) {
      console.warn('⚠️ AuthorizationProvider: AuthContext não encontrado. Renderizando loading...');
    }
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="w-8 h-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Inicializando segurança...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthorizationContext.Provider value={value}>
      {children}
    </AuthorizationContext.Provider>
  );
};

// Hook para usar o contexto de autorização
export const useAuthorization = () => {
  const context = useContext(AuthorizationContext);
  if (context === undefined) {
    const error = new Error('useAuthorization must be used within an AuthorizationProvider');
    console.error('❌ Erro de Contexto:', error);
    throw error;
  }
  return context;
};

